// levels/textlevel.ts — the shared shell for every AI-judged free-text level (L06/8/9/12).
//
// These levels differ in their prompt and in what a verdict MEANS (a strike, a retry, a
// trait), but the pipeline around the model is identical and safety-critical, so it lives
// in exactly one place:
//
//   1. crisis  — hard client override, gentle line, NO model call, passes (§10). This must
//                run on every judged level; a memory prompt (L09) is precisely where heavy
//                input arrives. Centralizing it is why this shell exists.
//   2. precheck — optional local rule that can short-circuit (L06's forbidden "rain").
//   3. abuse   — local deflection, passes with `spirited`, never quoted back (§9).
//   4. judged  — requestJudge fires on submit; ctx.verdictBeat masks the round trip (§10).
//                The level's `decide` maps the verdict to a LevelResult (strike / trait /
//                retry). Only the free-text answer ever leaves the device (non-negotiable #2).
//
// The judged verdict line is shown inside the beat (so its latency hides behind the
// considering animation); a strike, if any, is filed afterward by the state machine.

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl } from "@/levels/util.ts";
import { requestJudge, screenForCrisis, type JudgeResult } from "@/engine/judge.ts";
import { ABUSE } from "@/content/voice.ts";
import type { Act } from "@/theme/acts.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

/** Light abuse screen (§9): profanity aimed at the machine deflects, never escalates. */
export const ABUSE_RE =
  /\b(fuck|shit|screw you|stupid|idiot|shut up|hate you|dumb|bitch|asshole|moron)\b/i;

/** What a level decides once a judged answer comes back. */
export interface JudgeDecision {
  /** Re-prompt for ONE more try instead of finishing (L08's "too excellent"). The shell
   *  enforces a single retry regardless. */
  retry?: boolean;
  /** The LevelResult to finish with (ignored when `retry`). By convention `line` is omitted
   *  — the beat already spoke — but a level may set it to make main.ts speak again. */
  result?: LevelResult;
}

export interface TextLevelConfig {
  id: string;
  act: Act;
  title: string;
  /** Static prompt, or a builder resolved at mount (L08 wraps a prefetched machine answer). */
  prompt: string | ((ctx: LevelContext) => Promise<string> | string);
  placeholder: string;
  submit: string;
  charLimit: number;
  /** Offline / garble fallback voice lines, chosen deterministically from the answer (§12). */
  fallbacks: string[];
  /** Quiet note shown under the field after a retry is triggered. */
  retryHint?: string;
  /** Optional local pre-check that short-circuits before the model (L06's forbidden word). */
  precheck?: (answer: string) => LevelResult | null;
  /** Which line the verdict beat reveals, given the (settling) judge result. Defaults to the
   *  judge's own line; L08 overrides to show its local "too excellent" line on a retry. */
  beatLine?: (judge: JudgeResult, attempt: number) => string;
  /** Map a judged result to a decision. `attempt` is 0 on first submit, 1 after the retry. */
  decide: (judge: JudgeResult, answer: string, attempt: number) => JudgeDecision;
}

/** Build a judged free-text level from its config. */
export function textLevel(cfg: TextLevelConfig): Level {
  return {
    id: cfg.id,
    act: cfg.act,
    title: cfg.title,

    mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
      return runLevel(ctx, (done) => {
        const prompt = promptEl("");
        // The prompt may be async (a prefetched machine answer); render a placeholder first,
        // then fill it in. The field is usable immediately either way.
        void Promise.resolve(
          typeof cfg.prompt === "function" ? cfg.prompt(ctx) : cfg.prompt,
        ).then((text) => {
          if (!ctx.signal.aborted) prompt.textContent = text;
        });

        const field = el("textarea", "field field--area");
        field.maxLength = cfg.charLimit;
        field.rows = 3;
        field.placeholder = cfg.placeholder;
        field.spellcheck = false;
        field.setAttribute("aria-label", "Your answer");

        const count = el("div", "charcount");
        const updateCount = () => {
          count.textContent = `${field.value.length}/${cfg.charLimit}`;
        };
        updateCount();
        field.addEventListener("input", updateCount);

        const note = el("div", "field-note");
        note.hidden = true;

        const button = el("button", "btn");
        button.textContent = cfg.submit;

        host.append(prompt, field, count, note, button);
        window.setTimeout(() => field.focus(), 0);

        let busy = false;
        let attempt = 0;

        const submit = async () => {
          if (busy) return;
          const answer = field.value.trim();
          if (!answer) return;
          busy = true;
          button.disabled = true;
          field.disabled = true;

          // 1. crisis — never touches the model; gentle, passing, no cleverness (§10).
          const gentle = screenForCrisis(answer);
          if (gentle) return done({ pass: true, line: gentle });

          // 2. precheck — optional local short-circuit (e.g. L06's forbidden word).
          const pre = cfg.precheck?.(answer);
          if (pre) return done(pre);

          // 3. abuse — deflect in voice, pass, `spirited`; never quote it back (§9).
          if (ABUSE_RE.test(answer)) {
            return done({ pass: true, line: ABUSE.line, traits: ABUSE.traits });
          }

          // 4. judged — fire now; the considering animation hides the round trip (§10).
          const fallbackLine = pickFallback(cfg.fallbacks, answer);
          const pending = requestJudge({ level: cfg.id, answer, fallbackLine });
          const beatPending = pending.then((r) =>
            cfg.beatLine ? cfg.beatLine(r, attempt) : r.line,
          );
          await ctx.verdictBeat(fallbackLine, { pending: beatPending });
          const r = await pending; // already settled (requestJudge caps itself at 8s)

          const decision = cfg.decide(r, answer, attempt);
          if (decision.retry && attempt === 0) {
            // One free retry: reopen the field, drop a quiet note, let them go again.
            attempt = 1;
            busy = false;
            field.disabled = false;
            button.disabled = false;
            field.value = "";
            updateCount();
            if (cfg.retryHint) {
              note.textContent = cfg.retryHint;
              note.hidden = false;
            }
            field.focus();
            return;
          }
          done(decision.result ?? { pass: true, verdict: r.verdict, traits: r.traits });
        };

        button.addEventListener("click", () => void submit());
        field.addEventListener("keydown", (e) => {
          // Cmd/Ctrl+Enter submits; a bare Enter keeps its newline (answers may run long).
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void submit();
          }
        });
      });
    },
  };
}

/** Choose an offline fallback line deterministically from the answer (no render-path RNG). */
export function pickFallback(pool: string[], answer: string): string {
  if (pool.length === 0) return "";
  let h = 0;
  for (let i = 0; i < answer.length; i++) h = (h + answer.charCodeAt(i)) % pool.length;
  return pool[h]!;
}
